<template>
  <b-container fluid id="my-plan-billing-history">
    <div class="h5 font-weight-bold">Billing History</div>
    <b-row class="mt-4">
      <b-col md="5" lg="4" class="mb-3 mb-md-0">
        <b-input-group @click="toggleDateRangePicker">
          <datepicker :config="dateRangeConfig" placeholder="Select Date Range">
          </datepicker>
          <b-input-group-append>
            <i class="icon-calendar mr-1"></i>
            <i
              v-if="startDate && endDate"
              class="icon-clear-calendar"
              @click.stop="clearDateRangePicker"
            ></i>
          </b-input-group-append>
        </b-input-group>
      </b-col>
      <b-col md="4" lg="4">
        <b-input-group>
          <b-input-group-prepend>
            <i class="icon-search ml-2"></i>
          </b-input-group-prepend>
          <b-input
            v-model="searchKeyword"
            placeholder="Search"
            class="border-0"
          />
        </b-input-group>
      </b-col>
    </b-row>
    <div class="mt-4">
      <b-table
        hover
        id="billing-history-table"
        :class="['base-table mb-0', { 'unrounded-bottom': billingHistoryData.length > 0 }]"
        :items="billingHistoryData"
        :fields="fields"
        :empty-text="emptyTableText"
        :current-page="currentPage"
        show-empty
        responsive
      >
        <template slot="created_at" slot-scope="data">
          {{
            data.value ? $moment(data.value).format("MMM DD, YYYY hh:mm A") : ""
          }}
        </template>
        <template slot="charge_amount" slot-scope="data">
          <span class="font-weight-bold">
            &#8369;{{ data.value | currency }}
          </span>
        </template>
        <template slot="plan_name" slot-scope="data">
          <div v-if="data.value && data.item.billing_recurrence">
            <span class="font-weight-bold text-uppercase">
              {{ data.value }}
            </span>
            <template v-if="billingRecurrences[data.item.billing_recurrence]">
              &#8369;({{
                (billingRecurrences[data.item.billing_recurrence].multiplier *
                  data.item.plan_price)
                  | currency
              }}/{{ billingRecurrences[data.item.billing_recurrence].label }})
            </template>
          </div>
        </template>
        <template slot="card_brand" slot-scope="data">
          <div
            class="d-flex justify-content-start align-items-center"
            v-if="data.value && data.item.card_last_four_digit"
          >
            <div class="card-brand mr-2">
              <img
                v-if="data.value && cardBrands.includes(data.value)"
                :src="
                  require(`@/assets/images/cards/${data.item.card_brand}.png`)
                "
                alt="Card Brand"
              />
            </div>
            {{ data.item.card_last_four_digit }}
          </div>
        </template>
        <template slot="payment_status" slot-scope="data">
          <div class="payment-status mb-2" :class="data.value">
            {{ data.value }}
          </div>
          <a
            class="link-button"
            v-if="data.item.invoice_link"
            :href="data.item.invoice_link"
            target="_blank"
            rel="noopener"
          >
            View Invoice
          </a>
          <span
            class="px-2"
            v-if="
              data.item.receipt_link &&
              data.item.payment_status === 'paid' &&
              data.item.invoice_link
            "
          >
            |
          </span>
          <a
            class="link-button"
            v-if="data.item.receipt_link && data.item.payment_status === 'paid'"
            :href="data.item.receipt_link"
            target="_blank"
            rel="noopener"
          >
            View Receipt
          </a>
        </template>
        <template slot="upload_proof_of_payment" slot-scope="data">
          <b-row
            v-if="
              data.item.proof_of_payment &&
              (data.item.proof_of_payment_status === 'pending' ||
                data.item.proof_of_payment_status === 'approved')
            "
            no-gutters
            align-content="center"
            @click="viewFile(null, data.item.proof_of_payment)"
            style="cursor: pointer"
          >
            <b-col class="text-center view-file" align-self="center">
              <b-row no-gutters align-content="center" style="height: 100%">
                <b-col align-self="center">
                  {{
                    data.item.proof_of_payment.split("/")[
                      data.item.proof_of_payment.split("/").length - 1
                    ]
                  }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <div class="text-center" v-else-if="!files[data.item.transaction_id]">
            <i
              class="upload-csv-btn"
              @click="openLibrary(data.item)"
              v-b-popover.hover.top="
                'Only accept PNG, JPG, and PDF with a maximum of 5MB'
              "
            />
          </div>

          <b-row
            v-else
            no-gutters
            align-content="center"
            @click="viewFile(data.item.transaction_id)"
            style="cursor: pointer"
          >
            <b-col class="text-center view-file" align-self="center">
              <b-row no-gutters align-content="center" style="height: 100%">
                <b-col align-self="center">
                  {{ files[data.item.transaction_id].name }}
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <b-form-file
            v-model="files[data.item.transaction_id]"
            :ref="`file-${data.item.transaction_id}`"
            style="display: none"
            accept=".png,.jpg,.pdf"
            @input="validateFile(data.item.transaction_id)"
          />
        </template>
      </b-table>
      <template v-if="billingHistoryData.length > 0">
      <Pagination
        :currentPage="currentPage"
        :totalRows="totalRows"
        :maxPage="maxPage"
        @goToPage="getBillingHistory"
      />
    </template>
    </div>
  </b-container>
</template>

<script>
import Datepicker from "vue-bulma-datepicker";
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import notification from "@/services/notification";
import Pagination from "@/components/common/Pagination.vue";

export default {
  name: "MyPlanBilling",
  components: {
    Datepicker,
    Pagination,
  },
  data() {
    return {
      searchKeyword: "",
      dateRangePicker: null,
      fields: [
        {
          key: "created_at",
          label: "Date And Time",
          sortable: true,
        },
        {
          key: "transaction_number",
          sortable: false,
        },
        {
          key: "plan_name",
          label: "Transaction Name",
          sortable: false,
        },
        {
          key: "charge_amount",
          label: "Amount",
          sortable: false,
        },
        {
          key: "payment_status",
          sortable: false,
        },
        {
          key: "upload_proof_of_payment",
          sortable: false,
        },
      ],
      dataPerPage: 10,
      currentPage: 1,
      startDate: "",
      endDate: "",
      cardBrands: ["VISA", "MASTERCARD", "JCB"],
      billingRecurrences: {
        monthly: {
          multiplier: 1,
          label: "month",
        },
        quarterly: {
          multiplier: 3,
          label: "quarter",
        },
        bi_annual: {
          multiplier: 6,
          label: "6 months",
        },
        yearly: {
          multiplier: 12,
          label: "year",
        },
      },
      files: {},
    };
  },
  computed: {
    ...mapState("myPlan", ["loading", "billingHistory"]),
    ...mapState("accountProfile", ["detail"]),

    dateRangeConfig: {
      get: function () {
        const _this = this;

        return {
          altInput: true,
          altFormat: "M j, Y",
          dateFormat: "Y-m-d",
          static: true,
          mode: "range",
          onChange: function (value) {
            if (value.length == 2) {
              _this.startDate = _this.$moment(value[0]).format("YYYY-MM-DD");
              _this.endDate = _this.$moment(value[1]).format("YYYY-MM-DD");
              _this.getBillingHistory();
            }
          },
          onReady: function (_, __, instance) {
            _this.dateRangePicker = instance;
          },
        };
      },
    },
    emptyTableText() {
      const noRecords = "No Records Found";
      if (this.loading) return "Loading...";
      else if (this.searchKeyword)
        return `${noRecords} for ${this.searchKeyword}`;
      else return noRecords;
    },
    billingHistoryData() {
      if (_.has(this.billingHistory, "data") && this.billingHistory.data)
        return this.billingHistory.data;
      else return [];
    },
    pageFirstItem() {
      return this.billingHistory
        ? this.dataPerPage * this.billingHistory.prev_page + 1
        : 0;
    },
    pageLastItem() {
      return this.pageFirstItem > 0
        ? this.pageFirstItem + this.billingHistory.data.length - 1
        : 0;
    },
    totalRows() {
      return this.billingHistory && this.billingHistoryData.length > 0
        ? this.billingHistory.max_page * this.billingHistoryData.length
        : 1;
    },
    maxPage() {
      if (_.has(this.billingHistory, "max_page"))
        return this.billingHistory.max_page;
    }
  },
  methods: {
    ...mapActions("myPlan", ["uploadProofOfPayment"]),

    toggleDateRangePicker() {
      if (this.dateRangePicker) this.dateRangePicker.open();
    },
    clearDateRangePicker() {
      if (this.dateRangePicker) this.dateRangePicker.clear();

      this.startDate = "";
      this.endDate = "";
      this.getBillingHistory();
    },
    getBillingHistory(page = 1) {
      this.currentPage = page;
      this.$store.dispatch("myPlan/getBillingHistory", {
        page,
        parameters: {
          start_date: this.startDate,
          end_date: this.endDate,
          keyword: this.searchKeyword ? this.searchKeyword.toLowerCase() : "",
        },
      });
    },
    async validateFile(key) {
      if (!this.files[key]) return;

      const valid = ["image/png", "image/jpg", "image/jpeg", "application/pdf"];

      let hasError = false;

      if (!valid.includes(this.files[key].type)) {
        hasError = true;
        notification.warning("Upload PNG, JPG, and PDF file types only");
      } else if ((this.files[key].size / 1024 / 1024).toFixed(2) > 5) {
        hasError = true;
        notification.warning("Upload files with maximum 5Mb only");
      }

      if (hasError) {
        await this.$refs[`file-${key}`].reset();
        delete this.files[key];
        return;
      }

      this.uploadFile(key);
    },
    async uploadFile(key) {
      const payload = new FormData();
      const data = this.billingHistoryData.find(
        (el) => el.transaction_id === key
      );

      payload.append("file", await this.toBase64(this.files[key]));
      payload.append(
        "billing_date",
        this.$moment(data.created_at).format("YYYY-MM-DD")
      );
      payload.append("transaction_id", data.transaction_id);

      this.uploadProofOfPayment(payload)
        .then(() => {
          notification.success("Successfully uploaded a file.");
        })
        .catch(async () => {
          notification.warning("Oops, something went wrong.");

          await this.$refs[`file-${key}`].reset();
          delete this.files[key];
        });
    },
    toBase64(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (e) => {
          return resolve(e.target.result.split(",")[1]);
        };
      });
    },
    viewFile(key, url) {
      if (key) return window.open(URL.createObjectURL(this.files[key]));

      return window.open(url);
    },
    openLibrary(item) {
      if (item.card_brand) return;

      if (
        item.proof_of_payment_status === "rejected" ||
        !item.proof_of_payment_status
      )
        this.$refs[`file-${item.transaction_id}`].$el.childNodes[0].click();
    },
  },
  filters: {
    currency(value) {
      if (!value) return "0.00";

      return Number(parseFloat(value).toFixed(2)).toLocaleString("en", {
        minimumFractionDigits: 2,
      });
    },
  },
  watch: {
    searchKeyword() {
      this.onSearchkeywordChange();
    },
    detail() {
      if (this.detail.registration_channel === "Web") {
        if (
          this.fields[
            this.fields.indexOf(this.fields.find((e) => e.key === "card_brand"))
          ]
        ) {
          this.fields.splice(
            this.fields[
              this.fields.indexOf(
                this.fields.find((e) => e.key === "card_brand")
              )
            ],
            1
          );
        }

        this.fields.splice(4, 0, {
          key: "card_brand",
          label: "Payment Method",
          sortable: false,
        });
      }
    },
  },
  created() {
    this.getBillingHistory();
    this.onSearchkeywordChange = _.debounce(() => {
      this.getBillingHistory();
    }, 500);

    if (this.detail.registration_channel === "Web") {
      const index =
        this.fields[
          this.fields.indexOf(this.fields.find((e) => e.key === "card_brand"))
        ];

      if (index) {
        this.fields.splice(index, 1);
      }

      this.fields.splice(4, 0, {
        key: "card_brand",
        label: "Payment Method",
        sortable: false,
      });
    }
  },
};
</script>

<style lang="scss">
#my-plan-billing-history {
  padding: 50px 45px;
  @import url("~@/assets/scss/my-plan/_base.scss");

  .card-brand {
    height: 33px;
    width: 58px;
    border: 1px solid #cfd0d7;
    border-radius: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    img {
      width: 30px;
    }
  }
  .payment-status {
    border-radius: 5px;
    color: #ffffff;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 60px;
    height: 25px;
    text-transform: capitalize;
  }
  .paid {
    background-color: #1dc876;
  }
  .unpaid {
    background-color: #87929d;
  }
}
.payment-status {
  border-radius: 5px;
  color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 60px;
  height: 25px;
  text-transform: capitalize;
}
.paid {
  background-color: #1dc876;
}
.unpaid {
  background-color: #dc3545;
}
.link-button {
  color: #5182ff;
  font-family: MainFontBold, sans-serif;
  font-weight: normal;
  font-size: 14px;
  cursor: pointer;
}
.view-file {
  height: 50px;
  border: 3px solid #e2e6ee;
  color: black;
  font-size: 10px;
}
#billing-history-table td {
  min-width: 170px;
}
</style>
